/**
 * Tentpole editor modal — create/edit the cultural moments the calendar is
 * organized around. Sections follow what an editor decides, in order: the
 * moment, its window, a description, and (on edit) whether it's active. The
 * slug and the external-calendar flag rarely change, so they sit behind
 * "Advanced", whose collapsed line still shows the id.
 *
 * Slug is chosen at creation and frozen after (it's the Firestore doc id and
 * the FK on entries). Delete asks for an inline confirmation and is refused
 * server-side (409) while plans still reference the tentpole — deactivate
 * instead.
 */

import React, { useState } from 'react';
import { Button, Modal } from '../../ui';
import { createTentpole, deleteTentpole, updateTentpole } from '../../../services/tentpoleCalendarApi';
import { fieldStyle, labelStyle } from '../MediaPlans/formStyles';
import { Tentpole, TENTPOLE_CATEGORIES, TENTPOLE_CATEGORY_LABELS, TentpoleCategory } from './types';

interface TentpoleEditorProps {
  tentpole: Tentpole | null; // null = create
  /** Create-mode seed — the promote-custom-moment flow pre-fills from an entry. */
  preset?: { name?: string; category?: TentpoleCategory; startDate?: string; endDate?: string };
  onClose: () => void;
  /** Receives the saved tentpole (promote flow reassigns the source entry with it). */
  onSaved: (saved?: Tentpole) => void;
  notify: (kind: 'success' | 'error', title: string, message?: string) => void;
}

const hintStyle: React.CSSProperties = { fontSize: 12, lineHeight: 1.45, color: 'var(--fg-3)', marginTop: 6 };

const sectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

/** Outranks the uppercase field labels, so the sections read at a glance. */
const sectionHeadingStyle: React.CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display)',
  fontSize: 15,
  fontWeight: 700,
  color: 'var(--fg-1)',
};

const ruledSectionStyle: React.CSSProperties = {
  ...sectionStyle,
  paddingTop: 20,
  borderTop: '1px solid var(--border-subtle)',
};

/** A text link, so it opts out of the global button hover lift. */
const disclosureStyle: React.CSSProperties = {
  padding: 0,
  background: 'none',
  border: 'none',
  boxShadow: 'none',
  transform: 'none',
  color: 'var(--accent)',
  fontSize: 12.5,
  fontWeight: 600,
  fontFamily: 'var(--font-sans)',
  cursor: 'pointer',
};

const checkboxRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  fontSize: 13.5,
  cursor: 'pointer',
};

/** Mirrors the server's slugify (backend/src/services/takeoversFirestore.ts),
 *  so the suggestion is exactly the slug the server derives from the name. */
const suggestSlug = (name: string): string =>
  name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // strip combining diacritics left by NFKD
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

const TentpoleEditor: React.FC<TentpoleEditorProps> = ({ tentpole, preset, onClose, onSaved, notify }) => {
  const [name, setName] = useState(tentpole?.name ?? preset?.name ?? '');
  const [slug, setSlug] = useState(tentpole?.slug ?? (preset?.name ? suggestSlug(preset.name) : ''));
  const [slugTouched, setSlugTouched] = useState(!!tentpole);
  const [emoji, setEmoji] = useState(tentpole?.emoji ?? '');
  const [category, setCategory] = useState<TentpoleCategory>(tentpole?.category ?? preset?.category ?? 'culture');
  const [startDate, setStartDate] = useState(tentpole?.startDate ?? preset?.startDate ?? '');
  const [endDate, setEndDate] = useState(tentpole?.endDate ?? preset?.endDate ?? '');
  const [description, setDescription] = useState(tentpole?.description ?? '');
  const [externalVisible, setExternalVisible] = useState(tentpole?.externalVisible ?? false);
  const [active, setActive] = useState(tentpole?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const onNameChange = (next: string) => {
    setName(next);
    if (!tentpole && !slugTouched) setSlug(suggestSlug(next));
  };

  const save = async () => {
    if (!name.trim()) return notify('error', 'Name is required');
    if (!startDate || !endDate) return notify('error', 'Start and end dates are required');
    if (endDate < startDate) return notify('error', 'End date must be on or after the start date');
    setSaving(true);
    try {
      let saved: Tentpole;
      if (tentpole) {
        saved = await updateTentpole(tentpole.slug, {
          name: name.trim(),
          emoji: emoji.trim(),
          description: description.trim(),
          startDate,
          endDate,
          category,
          active,
          externalVisible,
        });
        notify('success', 'Tentpole updated', name.trim());
      } else {
        saved = await createTentpole({
          // An untouched suggestion is left to the server, which derives the
          // same slug from the name and runs its duplicate check on it.
          ...(slugTouched && slug.trim() ? { slug: slug.trim() } : {}),
          name: name.trim(),
          emoji: emoji.trim() || undefined,
          description: description.trim() || undefined,
          startDate,
          endDate,
          category,
          externalVisible,
        });
        notify('success', 'Tentpole created', name.trim());
      }
      onSaved(saved);
      onClose();
    } catch (err: any) {
      notify('error', 'Save failed', err?.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!tentpole) return;
    setDeleting(true);
    try {
      await deleteTentpole(tentpole.slug);
      notify('success', 'Tentpole deleted', tentpole.name);
      onSaved();
      onClose();
    } catch (err: any) {
      notify('error', 'Delete failed', err?.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Tentpole Calendar"
      title={tentpole ? `Edit ${tentpole.name}` : 'Add tentpole'}
      width={640}
      closeOnEsc={false}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', width: '100%' }}>
          <div>
            {tentpole && (
              <Button
                variant="ghost"
                style={{ color: 'var(--danger)' }}
                onClick={() => setConfirmingDelete(true)}
                disabled={saving || deleting}
              >
                Delete tentpole
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={saving || deleting}>
              {saving ? 'Saving…' : tentpole ? 'Save changes' : 'Create tentpole'}
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {tentpole && confirmingDelete && (
          <div
            role="alert"
            aria-label="Confirm tentpole deletion"
            style={{
              background: 'var(--danger-soft)',
              border: '1px solid var(--danger)',
              borderRadius: 'var(--radius-md)',
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div style={{ fontSize: 13, color: 'var(--fg-1)' }}>
              <strong>Delete “{tentpole.name}”?</strong>{' '}
              This works only while no plans use it; otherwise, uncheck Active instead.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="sm" variant="destructive" disabled={deleting} onClick={remove}>
                {deleting ? 'Deleting…' : 'Delete tentpole'}
              </Button>
              <Button size="sm" variant="ghost" disabled={deleting} onClick={() => setConfirmingDelete(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        <section style={sectionStyle}>
          <h3 style={sectionHeadingStyle}>Moment</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: 12 }}>
            <div>
              <label style={labelStyle}>Name</label>
              <input
                style={fieldStyle}
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                maxLength={120}
                placeholder="e.g. Halloween"
              />
            </div>
            <div>
              <label style={labelStyle}>Emoji</label>
              <input
                style={fieldStyle}
                value={emoji}
                onChange={(e) => setEmoji(e.target.value)}
                maxLength={8}
                placeholder="🎃"
              />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Category</label>
            <select
              style={{ ...fieldStyle, cursor: 'pointer', maxWidth: 260 }}
              value={category}
              onChange={(e) => setCategory(e.target.value as TentpoleCategory)}
            >
              {TENTPOLE_CATEGORIES.map((c) => (
                <option key={c} value={c}>{TENTPOLE_CATEGORY_LABELS[c]}</option>
              ))}
            </select>
          </div>
        </section>

        <section style={ruledSectionStyle}>
          <h3 style={sectionHeadingStyle}>Window</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <div>
              <label style={labelStyle}>Starts</label>
              <input
                type="date"
                style={fieldStyle}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>Ends</label>
              <input
                type="date"
                style={fieldStyle}
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </section>

        <section style={ruledSectionStyle}>
          <h3 style={sectionHeadingStyle}>Description</h3>
          <textarea
            style={{ ...fieldStyle, minHeight: 72, resize: 'vertical', fontFamily: 'inherit' }}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            aria-label="Description"
            placeholder="Internal curation notes — what content fits this moment, key dates inside the window…"
          />
        </section>

        {tentpole && (
          <section style={ruledSectionStyle}>
            <h3 style={sectionHeadingStyle}>Status</h3>
            <label style={checkboxRowStyle}>
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span>
                Active
                <div style={{ ...hintStyle, marginTop: 2 }}>
                  Inactive tentpoles stop accepting creator submissions and drop off the creator pages;
                  existing plans stay put.
                </div>
              </span>
            </label>
          </section>
        )}

        <div style={{ ...ruledSectionStyle, gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              style={disclosureStyle}
              aria-expanded={advancedOpen}
              onClick={() => setAdvancedOpen((open) => !open)}
            >
              {advancedOpen ? 'Hide advanced' : 'Advanced'}
            </button>
            {!advancedOpen && (
              <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>
                ID {slug || '—'}{externalVisible ? ' · on the external calendar' : ''}
              </span>
            )}
          </div>
          {advancedOpen && (
            <>
              <div>
                <label style={labelStyle}>Slug</label>
                {tentpole ? (
                  <code style={{ fontSize: 13 }}>{tentpole.slug}</code>
                ) : (
                  <input
                    style={fieldStyle}
                    value={slug}
                    onChange={(e) => { setSlug(e.target.value); setSlugTouched(true); }}
                    maxLength={80}
                    placeholder="halloween-2026"
                  />
                )}
                <div style={hintStyle}>
                  {tentpole
                    ? 'Fixed at creation; plans refer to it.'
                    : 'A permanent id, suggested from the name. Change it before saving if needed.'}
                </div>
              </div>

              <label style={checkboxRowStyle}>
                <input
                  type="checkbox"
                  checked={externalVisible}
                  onChange={(e) => setExternalVisible(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span>
                  Visible on the external client calendar
                  <div style={{ ...hintStyle, marginTop: 2 }}>
                    Reserved for the future brand-facing calendar — nothing is shared externally yet.
                  </div>
                </span>
              </label>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TentpoleEditor;
